import React, { useState, useEffect } from 'react';
import { TooltipHost, DirectionalHint } from '@fluentui/react';
import styles from './ProfilePhotoStack.module.css';

export interface CandidateReportPhoto {
  photo_data?: string;
  thumbnail_path?: string;
  candidatereport_data?: {
    candidateName: string;
    profilePhotoFileName?: string;
  };
}

export interface TrafficLightPhoto {
  url?: string;
  name: string;
  error?: boolean;
}

export interface ProfilePhotoStackProps {
  report: {
    metadata?: {
      profile_photos?: TrafficLightPhoto[];
    };
    details?: CandidateReportPhoto;
  };
}

export interface PhotoData {
  url?: string;
  name: string;
  error?: boolean;
}

export const ProfilePhotoStack: React.FC<ProfilePhotoStackProps> = ({ report }) => {
  const [photos, setPhotos] = useState<PhotoData[]>([]);

  // Design constants
  const PHOTO_SIZE = 48;
  const OVERLAP = PHOTO_SIZE * 0.4;
  const BORDER_WIDTH = 2;

  const tooltipProps = {
    calloutProps: {
      styles: {
        root: {
          padding: '8px 12px',
          backgroundColor: '#fff',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        },
        beak: {
          backgroundColor: '#fff',
        },
      },
    },
    styles: {
      root: { display: 'inline-block' },
      subText: {
        fontSize: '12px',
        fontWeight: '500',
        color: '#323130',
        margin: 0,
        lineHeight: '16px',
      },
    },
    delay: 150,
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  useEffect(() => {
    try {
      // Handle traffic light report format
      if (report.metadata?.profile_photos) {
        (async () => {
          const inputs = report.metadata!.profile_photos!;
          const loaded: PhotoData[] = await Promise.all(
            inputs.map(async p => {
              try {
                if (!p.url || p.url.startsWith('data:')) {
                  return { name: p.name, url: p.url, error: p.error } as PhotoData;
                }
                const response = await fetch(p.url);
                if (!response.ok) {
                  return { name: p.name, error: true } as PhotoData;
                }
                const blob = await response.blob();
                const objectUrl = URL.createObjectURL(blob);
                return { name: p.name, url: objectUrl, error: false } as PhotoData;
              } catch {
                return { name: p.name, error: true } as PhotoData;
              }
            }),
          );
          setPhotos(loaded);
        })();
        return;
      }

      // Handle candidate report format
      if (report.details?.candidatereport_data) {
        const { photo_data, candidatereport_data, thumbnail_path } = report.details;
        if (candidatereport_data.candidateName) {
          const candidateName = candidatereport_data.candidateName;

          // If we already have a data URL, use it directly (no auth needed)
          if (photo_data && photo_data.startsWith('data:')) {
            setPhotos([{ url: photo_data, name: candidateName, error: false }]);
            return;
          }

          // Build the API path for the image without encoding slashes
          const path = thumbnail_path
            ? `/api/profile-photo/${encodeURI(thumbnail_path)}`
            : (candidatereport_data.profilePhotoFileName
                ? `/api/profile-photo/${encodeURI(candidatereport_data.profilePhotoFileName)}`
                : undefined);

          if (!path) {
            setPhotos([{ name: candidateName, error: true }]);
            return;
          }

          // Fetch the image via fetch so our auth interceptor adds the Bearer token
          (async () => {
            try {
              const response = await fetch(path);
              if (!response.ok) {
                setPhotos([{ name: candidateName, error: true }]);
                return;
              }
              const blob = await response.blob();
              const objectUrl = URL.createObjectURL(blob);
              setPhotos([{ url: objectUrl, name: candidateName, error: false }]);
            } catch (e) {
              setPhotos([{ name: candidateName, error: true }]);
            }
          })();
        }
      }
    } catch (error) {
      console.error('Error processing profile photos:', error);
    }
  }, [report]);

  // Return null only if we have no photos AND no candidate name
  if (!photos.length && !report.details?.candidatereport_data?.candidateName) return null;

  const shouldShowCounter = photos.length > 4;
  const displayPhotos = shouldShowCounter ? photos.slice(0, 3) : photos.slice(0, 4);
  const remainingCount = photos.length - (shouldShowCounter ? 3 : 4);

  return (
    <div className={styles.photoStack}>
      {displayPhotos.map((photo, index) => (
        <TooltipHost
          key={photo.name}
          content={photo.name}
          directionalHint={DirectionalHint.bottomCenter}
          {...tooltipProps}
        >
          <div
            className={styles.photo}
            style={{
              marginLeft: index > 0 ? `-${OVERLAP}px` : '0',
              zIndex: photos.length - index,
            }}
            onMouseEnter={e => {
              const target = e.currentTarget;
              target.style.transform = 'scale(1.35)';
              target.style.zIndex = '30';
              target.style.boxShadow = '0 8px 16px rgba(0,0,0,0.12)';
              target.style.border = `${BORDER_WIDTH}px solid white`;
            }}
            onMouseLeave={e => {
              const target = e.currentTarget;
              target.style.transform = '';
              target.style.zIndex = String(photos.length - index);
              target.style.boxShadow = '';
              target.style.border = `${BORDER_WIDTH}px solid #E1DFDD`;
            }}
          >
            {photo.error || !photo.url ? (
              <div className={styles.placeholder}>{getInitials(photo.name)}</div>
            ) : (
              <img
                src={photo.url}
                alt={photo.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
                onError={e => {
                  const target = e.currentTarget;
                  const parent = target.parentElement;
                  if (parent) {
                    const placeholder = document.createElement('div');
                    placeholder.className = styles.placeholder;
                    placeholder.textContent = getInitials(photo.name);
                    parent.replaceChild(placeholder, target);
                  }
                }}
              />
            )}
          </div>
        </TooltipHost>
      ))}
      {remainingCount > 1 && (
        <TooltipHost
          content={`${remainingCount} more team members`}
          directionalHint={DirectionalHint.bottomCenter}
          {...tooltipProps}
        >
          <div className={styles.counter}>+{remainingCount}</div>
        </TooltipHost>
      )}
    </div>
  );
};
